/**
 * PharmacyAuthPage.tsx — /auth-pharmacy, the dispensing console's front door.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * TWO FACTORS, ONE REQUEST. The dispensing notification lands in a SHARED
 * mailbox (team@pharmacy-xpress.co.uk), so the emailed link proves possession of
 * that mailbox — the organisation — and nothing about which human is at the
 * keyboard. The pharmacist's own pre-issued 12-character code is what identifies
 * the individual. GPhC expects a dispensing record attributable to a NAMED
 * registered professional, never a shared login, so both travel in ONE request
 * and there is no intermediate state a caller could reach on the link alone.
 *
 * THE LINK TOKEN RIDES IN THE URL FRAGMENT, NEVER THE QUERY STRING. A query
 * string is written verbatim into load-balancer, WAF and nginx access logs on
 * the very first page load, and no client-side rewrite can retract it — the
 * token would be a live auth factor sitting in plain text in three log estates.
 * A fragment is never sent to the server at all. We additionally scrub it out of
 * the address bar as soon as it is read (history.replaceState), so it does not
 * survive in browser history, in a screen-share, or in a URL someone copies out
 * of the address bar to ask a colleague for help.
 *
 * UNIFORM FAILURE. Every failure — bad code, expired link, already-used link,
 * unknown pharmacist, throttled, server error — renders the SAME message. A
 * message that distinguished them would turn this screen into an oracle: an
 * attacker holding a leaked link could brute-force the code and be told when the
 * link was the problem instead, or vice versa. That is also why the API call is
 * made without `parseError` — there is deliberately no body to render.
 *
 * ACCESSIBILITY: a real `<label>` bound to the input (never placeholder-only),
 * the failure text in a `role="alert"` region that says what to DO next, the
 * input pointed at it via aria-describedby, and the kit's global :focus-visible
 * ring. R28: category-level copy only — no POM name appears here.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { pharmacyLogin } from '../api/pharmacy';
import { readHashToken, scrubHashFromAddressBar } from '../lib/hashToken';
import { Alert, Button, Card, Field, Help, PageFrame, TextInput, Title } from '../ui/kit';
import { getPharmacySession, setPharmacySession } from './session';
import { TestSystemNotice } from './TestSystemNotice';

/** The code is server-minted at a fixed width — the contract pins 12 exactly. */
export const PHARMACIST_CODE_LENGTH = 12;

/**
 * The ONE failure message. Deliberately says what to do next without naming
 * which factor failed (see the module doc).
 */
const UNIFORM_FAILURE =
  'We could not sign you in. Check your code, then open the most recent link from your dispensing email and try again.';

/**
 * Pull the link token out of `location.hash`.
 *
 * The reader itself moved to `lib/hashToken` when the end-of-term re-subscribe
 * confirmation link became the second lane to need it — the fragment discipline
 * documented above is identical for both and must not drift. This stays exported
 * under its original name because it is this page's own vocabulary (and its
 * spec's).
 */
export const readLinkTokenFromHash = readHashToken;

export function PharmacyAuthPage() {
  const navigate = useNavigate();
  // Read the fragment ONCE, during the first render, before anything can
  // navigate: the scrub below removes it from the URL, so a later read would
  // find nothing.
  const [linkToken] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : readLinkTokenFromHash(window.location.hash),
  );
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Scrub the live auth factor out of the address bar the moment we hold it —
  // it must not linger in browser history, a copied URL or a screen-share.
  useEffect(() => {
    if (!linkToken) return;
    scrubHashFromAddressBar();
  }, [linkToken]);

  // A pharmacist who already holds a live session and arrived WITHOUT a link
  // (a bookmark, a back navigation) goes straight to the queue. Arriving WITH a
  // link always re-authenticates: the same shared mailbox serves the whole
  // dispensary, so a new link may well be a different person.
  const hasLiveSession = getPharmacySession() !== null;
  useEffect(() => {
    if (!linkToken && hasLiveSession) navigate('/pharmacy', { replace: true });
  }, [linkToken, hasLiveSession, navigate]);

  const trimmedCode = code.trim();
  // A LENGTH check only — it describes what the person typed, not which factor
  // the server disliked, so it is not an oracle. It saves a pointless round trip.
  const codeReady = trimmedCode.length === PHARMACIST_CODE_LENGTH;
  const errorId = useMemo(() => 'pharmacy-auth-error', []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!linkToken || !codeReady || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const result = await pharmacyLogin({ linkToken, code: trimmedCode });
      setPharmacySession(result);
      navigate('/pharmacy', { replace: true });
    } catch {
      // EVERY failure lands here and renders the SAME message — see the module
      // doc. The code is cleared so a retry starts clean on a shared keyboard.
      setFailed(true);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    // `PageFrame narrow` is what /auth uses, so this page inherits the same
    // column width, gutters and rhythm rather than a hand-rolled approximation of
    // them. The brand lockup that used to sit here has gone: AppShell now paints
    // the green masthead (which carries the logo) and the legal footer, so a
    // second centred lockup would be duplicate chrome.
    <PageFrame narrow>
      <div>
        <Title>Pharmacy sign-in</Title>

        {/* Test hosts only — renders nothing on the live one. A stale bookmark to
            the test system is a dead end (a live email's link cannot work there),
            so the page says so in words; see TestSystemNotice for why the
            decorative staging ribbon is not enough. */}
        <TestSystemNotice />

        {!linkToken ? (
          // No usable fragment: there is nothing to submit, so no form is shown.
          // This reveals nothing — the browser can see its own URL either way.
          <>
            <Help>
              Open the sign-in link from your dispensing email to continue. The link works once and expires,
              so please use the most recent one.
            </Help>
            <Alert tone="info" role="status">
              For security, your sign-in link is not part of the web address you can copy from here. If you
              have mislaid it, ask the GP Service team to send a new one.
            </Alert>
          </>
        ) : (
          <>
            <Help>
              Your link confirms the pharmacy. Enter your own pharmacist code so the dispensing record names
              you.
            </Help>
            <Card>
              <form onSubmit={onSubmit} aria-label="Pharmacy sign-in">
                <Field
                  label="Your pharmacist code"
                  htmlFor="pharmacist-code"
                  hint={`${PHARMACIST_CODE_LENGTH} characters, issued to you personally. Never share it.`}
                >
                  <TextInput
                    id="pharmacist-code"
                    name="pharmacistCode"
                    type="password"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={PHARMACIST_CODE_LENGTH}
                    autoComplete="one-time-code"
                    // The code is a credential, so the browser must not
                    // autocorrect, capitalise or spellcheck what is typed.
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    disabled={busy}
                    aria-describedby={failed ? errorId : undefined}
                    aria-invalid={failed || undefined}
                  />
                </Field>

                {failed ? (
                  <Alert tone="danger" role="alert" id={errorId} style={{ marginBottom: 'var(--gps-s-3)' }}>
                    {UNIFORM_FAILURE}
                  </Alert>
                ) : null}

                <Button type="submit" variant="cta" disabled={!codeReady || busy}>
                  {busy ? 'Signing you in…' : 'Sign in'}
                </Button>
              </form>
            </Card>
            <p style={{ marginTop: 'var(--gps-s-4)', fontSize: '13px', color: 'var(--gps-muted)' }}>
              Every dispense is recorded against the pharmacist who confirmed it.
            </p>
          </>
        )}
      </div>
    </PageFrame>
  );
}
